/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {createOfflineCanRequest, getOfflineCanRequest} from '@ion/core/src/services/Connect';
import {
  CreateOfflineCanRequestPayload,
  OfflineCanRequestResult,
  OfflineCanRequestStatus
} from '@ion/core/src/types/services';
import Alert from '@ion/dls/src/components/alert/Alert';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {Skeleton} from '@ion/dls/src/components/skeleton/Skeleton';
import {exportCSV} from '@ion/dls/src/grid/Exporter';
import {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import UserNotificationDialog from '@ion/dls/src/notifications/UserNotificationDialog';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {bulkRetryCanRequests, cancelCanRequest, retryCanRequest} from '../canrequests/CanRequestUtils';
import {
  canRequestSummaryAccordionExpanded,
  canRequestsViewMode,
  selectedVehicle,
  setCanRequestsViewMode
} from '../manager/store/store';
import CanRequestConfirmDialog from './CanRequestConfirmDialog';
import CanRequestCreateView from './CanRequestCreateView';
import CanRequestDetailsGrid from './CanRequestDetailsGrid';
import CanRequestJsonView from './CanRequestJsonView';
import CanRequestsGrid from './CanRequestsGrid';
import CanRequestsToolbar from './CanRequestsToolbar';
import useCleanSessionGate from './useCleanSessionGate';
import useVehicleCanRequestsData from './useVehicleCanRequestsData';

const TRANSLATIONS = 'vehicle-manager:vehicleHistoryGrid';

export interface VehicleCanRequestsTabProps {
  canRequestEnabled: boolean;
  canViewAuditLog?: boolean;
}

const useStyles = makeStyles((_theme: DLSTheme) => ({
  flexStyleNoMargin : {
    display    : 'flex',
    margin     : 0,
    alignItems : 'center',
    width      : '100%',
    flexShrink : 0
  },
  canRequestsSection : {
    display       : 'flex',
    flexDirection : 'column',
    flex          : 1,
    minHeight     : 0,
    height        : '100%'
  },
  detailsHeaderRow : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    width          : '100%'
  },
  detailsExportAction : {
    marginLeft : '16px'
  },
  detailsSkeletonContainer : {
    width         : '100%',
    display       : 'flex',
    flexDirection : 'column',
    minHeight     : '560px'
  },
  summarySkeleton : {
    marginBottom : '8px'
  },
  breadcrumbBack : {
    margin    : '12px 0px',
    minHeight : '32px',
    maxHeight : '32px',
    '&:hover' : {
      cursor : 'default'
    },
    '& span' : {
      color : dlsColor('grey3')
    }
  },
  breadcrumbBackLink : {
    color     : `${dlsColor('grey2')} !important`,
    cursor    : 'pointer',
    '&:hover' : {
      color : `${dlsColor('black')} !important`
    }
  },
  breadcrumbCurrent : {
    color         : `${dlsColor('grey3')} !important`,
    cursor        : 'default',
    pointerEvents : 'none'
  },
  breadcrumbChevron : {
    color : dlsColor('grey3')
  }
}));

/**
 * Coordinates CAN Requests tab state, data loading, and mode-specific rendering.
 *
 * ```typescript
 * <VehicleCanRequestsTab canRequestEnabled={canRequestEnabled} />
 * ```
 *
 * @param   canRequestEnabled Enables CAN request query loading for this tab.
 * @param   canViewAuditLog Determines whether Scout Audit Log is shown.
 * @returns The CAN Requests tab coordinator component.
 */
const VehicleCanRequestsTab = ({canRequestEnabled, canViewAuditLog = false}: VehicleCanRequestsTabProps) => {
  const dispatch                                              = useDispatch();
  const classes                                               = useStyles();
  const [retryingRequestId, setRetryingRequestId]             = useState<string | null>(null);
  const [pendingRetryRequest, setPendingRetryRequest]         = useState<OfflineCanRequestResult | null>(null);
  const [cancellingRequestId, setCancellingRequestId]         = useState<string | null>(null);
  const [pendingCancelRequest, setPendingCancelRequest]       = useState<OfflineCanRequestResult | null>(null);
  const [pendingBulkRetry, setPendingBulkRetry]               = useState<boolean>(false);
  const [bulkRetrying, setBulkRetrying]                       = useState<boolean>(false);
  const [selectedRequests, setSelectedRequests]               = useState<OfflineCanRequestResult[]>([]);
  const [selectedRequestDetails, setSelectedRequestDetails]   = useState<OfflineCanRequestResult | null>(null);
  const [selectedCanRequestId, setSelectedCanRequestId]       = useState<string | null>(null);
  const [isLoadingRequestDetails, setIsLoadingRequestDetails] = useState<boolean>(false);
  const [appliedCanStartDate, setAppliedCanStartDate]         = useState<string | null>(null);
  const [appliedCanEndDate, setAppliedCanEndDate]             = useState<string | null>(null);
  const canRequestDetailsGridRef                              = useRef<IonGridAPI>(null);
  const canRequestsGridRef                                    = useRef<IonGridAPI>(null);

  const selectedCount = selectedRequests.length;

  const onSelectionChange = useCallback((requests: OfflineCanRequestResult[]) => {
    setSelectedRequests(requests);
  }, []);

  const onClearSelection = useCallback(() => {
    canRequestsGridRef.current?.deselectAll?.();
  }, []);

  const canEsnOptions = useMemo(() => {
    const esn = selectedVehicle.value?.esn;
    const values = Array.isArray(esn) ? esn : [esn];
    return values
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean)
      .map((value) => ({label: value, value}));
  }, [selectedVehicle.value?.esn]);
  const [selectedCanEsn, setSelectedCanEsn] = useState<string>('');

  useEffect(() => {
    const fallback = canEsnOptions?.[0]?.value || '';
    if (!selectedCanEsn || !canEsnOptions.some((option) => option.value === selectedCanEsn)) {
      setSelectedCanEsn(fallback);
    }
  }, [canEsnOptions, selectedCanEsn]);

  const {cleanSessionTooltip} = useCleanSessionGate(canRequestEnabled ? selectedCanEsn : '');

  const {
    loadCanRequestRows,
    refresh,
    onRefresh: onOfflineCanRequestsRefresh,
    fetching: isFetchingOfflineCanRequests,
    paging,
    noResultsDescription,
    maxSelection,
    hasReachedPendingLimit
  } = useVehicleCanRequestsData({
    esn       : selectedCanEsn,
    startDate : appliedCanStartDate,
    endDate   : appliedCanEndDate,
    enabled   : canRequestEnabled
  });

  const showNoResults = noResultsDescription !== null && !isFetchingOfflineCanRequests;

  const {
    canRequestsTab = '',
    canRequestsGrid = {},
    canRequestDetailsGrid = {},
    canRequestsError = {}
  } = (translate(TRANSLATIONS, {returnObjects: true}) || {});
  const {
    createCanRequest = '',
    retryConfirmTitle = '',
    retryConfirmMessage = '',
    cancelConfirmTitle = '',
    cancelConfirmMessage = ''
  } = canRequestsGrid || {};
  const {exportDisabledTooltip = ''} = canRequestDetailsGrid || {};
  const {createDisabledTooltip = ''} = canRequestsGrid || {};
  const {
    title: canRequestsErrorTitle = '',
    message: canRequestsErrorMessage = ''
  } = canRequestsError || {};

  const onCanRequestRetry = useCallback(async(request: OfflineCanRequestResult) => {
    await retryCanRequest({
      request,
      dispatch,
      setRetryingRequestId,
      selectedCanEsn,
      onSuccess : onOfflineCanRequestsRefresh
    });
  }, [dispatch, onOfflineCanRequestsRefresh, selectedCanEsn]);

  const onOpenCanRequestRetryConfirmation = useCallback((request: OfflineCanRequestResult) => {
    setPendingRetryRequest(request);
  }, []);

  const onCloseCanRequestRetryConfirmation = useCallback(() => {
    setPendingRetryRequest(null);
  }, []);

  const onConfirmCanRequestRetry = useCallback(async() => {
    if (pendingRetryRequest) {
      await onCanRequestRetry(pendingRetryRequest);
      setPendingRetryRequest(null);
    }
  }, [onCanRequestRetry, pendingRetryRequest]);

  const onCanRequestCancel = useCallback(async(request: OfflineCanRequestResult) => {
    await cancelCanRequest({
      request,
      dispatch,
      setCancellingRequestId,
      onSuccess : onOfflineCanRequestsRefresh
    });
  }, [dispatch, onOfflineCanRequestsRefresh]);

  const onOpenCanRequestCancelConfirmation = useCallback((request: OfflineCanRequestResult) => {
    setPendingCancelRequest(request);
  }, []);

  const onCloseCanRequestCancelConfirmation = useCallback(() => {
    setPendingCancelRequest(null);
  }, []);

  const onConfirmCanRequestCancel = useCallback(async() => {
    if (pendingCancelRequest) {
      await onCanRequestCancel(pendingCancelRequest);
      setPendingCancelRequest(null);
    }
  }, [onCanRequestCancel, pendingCancelRequest]);

  const onOpenBulkRetryConfirmation = useCallback(() => {
    setPendingBulkRetry(true);
  }, []);

  const onCloseBulkRetryConfirmation = useCallback(() => {
    setPendingBulkRetry(false);
  }, []);

  const onConfirmBulkRetry = useCallback(async() => {
    await bulkRetryCanRequests({
      requests  : selectedRequests,
      dispatch,
      setBulkRetrying,
      selectedCanEsn,
      onClearSelection,
      onSuccess : function() {
        setPendingBulkRetry(false);
        onOfflineCanRequestsRefresh?.();
      }
    });
  }, [selectedRequests, dispatch, selectedCanEsn, onClearSelection, onOfflineCanRequestsRefresh]);

  const onCanRequestViewDetails = useCallback(async(request: OfflineCanRequestResult) => {
    if (!request?.requestId) {
      return;
    }
    setCanRequestsViewMode?.('details');
    const shouldFetchDetails = !request?.canRequest || !Array.isArray(request?.canResponses);
    try {
      setSelectedCanRequestId(request.requestId);
      setSelectedRequestDetails(request);
      if (!shouldFetchDetails) {
        return;
      }
      setIsLoadingRequestDetails(true);
      const details = await getOfflineCanRequest(request.requestId);
      const selectedRequest = details?.response?.results?.[0] || request;
      setSelectedRequestDetails(selectedRequest);
    } catch (err) {
      error(err);
    } finally {
      setIsLoadingRequestDetails(false);
    }
  }, []);

  const onBackToCanRequests = useCallback(() => {
    setCanRequestsViewMode?.('view');
    setSelectedCanRequestId(null);
    setIsLoadingRequestDetails(false);
    setSelectedRequestDetails(null);
  }, []);

  const onCanRequestsSearch = useCallback((draftCanStartDate, draftCanEndDate) => {
    const formattedStart = draftCanStartDate
      ? moment(draftCanStartDate).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : null;
    const formattedEnd = draftCanEndDate
      ? moment(draftCanEndDate).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : null;
    setAppliedCanStartDate(formattedStart);
    setAppliedCanEndDate(formattedEnd);
  }, []);

  const onClearCanRequestsFilters = useCallback(() => {
    setAppliedCanStartDate(null);
    setAppliedCanEndDate(null);
  }, []);

  const onCanEsnChange = useCallback((event) => {
    setSelectedCanEsn(event?.target?.value || '');
  }, []);

  const onCreateCanRequest = useCallback(() => {
    setCanRequestsViewMode?.('create');
  }, []);

  const onCancelCreateCanRequest = useCallback(() => {
    setCanRequestsViewMode?.('view');
  }, []);

  const onCanRequestCreateSubmitted = useCallback(async(payload: CreateOfflineCanRequestPayload) => {
    await createOfflineCanRequest(payload);
    setCanRequestsViewMode?.('view');
    onOfflineCanRequestsRefresh?.();
  }, [onOfflineCanRequestsRefresh]);

  const onCanRequestViewJson = useCallback((request: OfflineCanRequestResult) => {
    setSelectedRequestDetails(request);
    setCanRequestsViewMode?.('json');
  }, []);

  const onBackToCanRequestsFromJson = useCallback(() => {
    setCanRequestsViewMode?.('view');
    setSelectedRequestDetails(null);
  }, []);

  const onExportCanRequestDetails = useCallback(() => {
    const exportData = canRequestDetailsGridRef.current?.getAllDisplayedRowData?.();
    if (!exportData?.items || !exportData?.columns) {
      return;
    }
    exportCSV({
      columns          : exportData.columns,
      items            : exportData.items,
      entity           : 'can-request-details',
      includeTimestamp : true
    });
  }, []);

  const isCreatingCanRequest         = canRequestsViewMode.value === 'create';
  const isViewingCanRequestJson      = canRequestsViewMode.value === 'json';
  const isViewingCanRequestDetails   = canRequestsViewMode.value === 'details' && !!selectedCanRequestId;
  const canExportCanRequestDetails   = `${selectedRequestDetails?.status || ''}` ===
    OfflineCanRequestStatus.Completed;
  const pendingLimitTooltip          = hasReachedPendingLimit ? createDisabledTooltip : '';
  const createDisabledReason         = cleanSessionTooltip || pendingLimitTooltip;
  const detailsSummarySkeletonHeight = canRequestSummaryAccordionExpanded.value ? 210 : 48;

  return (
    <div className={classes.canRequestsSection}>
      <Render.If condition={
        !isCreatingCanRequest &&
      !isViewingCanRequestJson &&
      !isViewingCanRequestDetails &&
      showNoResults
      }>
        <Alert type="neutral"
          title={canRequestsErrorTitle}
          message={noResultsDescription || canRequestsErrorMessage}
          gutters={false} />
      </Render.If>
      <div className={classes.flexStyleNoMargin}>
        <Render.If condition={isCreatingCanRequest && !isViewingCanRequestDetails}>
          <Breadcrumb height='32px'
            breadcrumbClass={classes.breadcrumbBack}
            iconClick={onCancelCreateCanRequest}
            icon={<ChevronLeft className={classes.breadcrumbChevron} />}>
            <span className={classes.breadcrumbBackLink}
              onClick={onCancelCreateCanRequest}>
              {canRequestsTab}
            </span>
            <span className={classes.breadcrumbCurrent}>
              {createCanRequest}
            </span>
          </Breadcrumb>
        </Render.If>
        <Render.If condition={isViewingCanRequestDetails}>
          <div className={classes.detailsHeaderRow}>
            <Breadcrumb height='32px'
              breadcrumbClass={classes.breadcrumbBack}
              iconClick={onBackToCanRequests}
              icon={<ChevronLeft className={classes.breadcrumbChevron} />}>
              <span className={classes.breadcrumbBackLink}
                onClick={onBackToCanRequests}>
                {canRequestsTab}
              </span>
              <span className={classes.breadcrumbCurrent}>
                {translate('common:details')}
              </span>
            </Breadcrumb>
            <div className={classes.detailsExportAction}>
              <CSVExportButton onExport={onExportCanRequestDetails}
                enableExport={!canExportCanRequestDetails}
                customTooltip={!canExportCanRequestDetails
                  ? exportDisabledTooltip
                  : undefined} />
            </div>
          </div>
        </Render.If>
        <Render.If condition={!isViewingCanRequestDetails && !isCreatingCanRequest && !isViewingCanRequestJson}>
          <CanRequestsToolbar canEsnOptions={canEsnOptions}
            selectedCanEsn={selectedCanEsn}
            appliedCanStartDate={appliedCanStartDate}
            appliedCanEndDate={appliedCanEndDate}
            disableCreateCanRequest={Boolean(createDisabledReason)}
            disabledCreateCanRequestTooltip={createDisabledReason}
            onCanEsnChange={onCanEsnChange}
            onCanRequestsSearch={onCanRequestsSearch}
            onClearCanRequestsFilters={onClearCanRequestsFilters}
            onOfflineCanRequestsRefresh={onOfflineCanRequestsRefresh}
            onCreateCanRequest={onCreateCanRequest} />
        </Render.If>
      </div>
      <Render.If condition={isViewingCanRequestJson}>
        <CanRequestJsonView requestDetails={selectedRequestDetails}
          onBack={onBackToCanRequestsFromJson} />
      </Render.If>
      <Render.If condition={isCreatingCanRequest && !isViewingCanRequestDetails}>
        <CanRequestCreateView esn={selectedCanEsn}
          onCancel={onCancelCreateCanRequest}
          onSubmitted={onCanRequestCreateSubmitted}
          submitDisabledReason={cleanSessionTooltip} />
      </Render.If>
      <Render.If condition={isViewingCanRequestDetails && isLoadingRequestDetails}>
        <div className={classes.detailsSkeletonContainer}>
          <div className={classes.summarySkeleton}>
            <Skeleton variant='rect'
              animation='wave'
              width='100%'
              height={detailsSummarySkeletonHeight} />
          </div>
          <CanRequestDetailsGrid ref={canRequestDetailsGridRef}
            requestDetails={selectedRequestDetails || {} as OfflineCanRequestResult}
            loading />
        </div>
      </Render.If>
      <Render.If condition={isViewingCanRequestDetails && !!selectedRequestDetails}>
        <CanRequestDetailsGrid ref={canRequestDetailsGridRef}
          requestDetails={selectedRequestDetails || {} as OfflineCanRequestResult} />
      </Render.If>
      <Render.If condition={!isViewingCanRequestDetails && !isCreatingCanRequest && !isViewingCanRequestJson}>
        <CanRequestsGrid ref={canRequestsGridRef}
          loadItems={loadCanRequestRows}
          refresh={refresh}
          pageSize={paging.pageSize}
          setGridPageSize={paging.setPageSize}
          onRetry={onOpenCanRequestRetryConfirmation}
          retryingRequestId={retryingRequestId}
          onCancel={onOpenCanRequestCancelConfirmation}
          cancellingRequestId={cancellingRequestId}
          onViewJson={onCanRequestViewJson}
          onViewDetails={onCanRequestViewDetails}
          selectedCount={selectedCount}
          maxSelection={maxSelection}
          bulkRetrying={bulkRetrying}
          onSelectionChange={onSelectionChange}
          onBulkRetry={onOpenBulkRetryConfirmation}
          canViewAuditLog={canViewAuditLog}
          selectionEnabled />
      </Render.If>
      <CanRequestConfirmDialog action="retry"
        show={!!pendingRetryRequest}
        title={retryConfirmTitle}
        message={retryConfirmMessage}
        loading={!!retryingRequestId}
        onConfirm={onConfirmCanRequestRetry}
        onClose={onCloseCanRequestRetryConfirmation} />
      <CanRequestConfirmDialog action="cancel"
        show={!!pendingCancelRequest}
        title={cancelConfirmTitle}
        message={cancelConfirmMessage}
        loading={!!cancellingRequestId}
        onConfirm={onConfirmCanRequestCancel}
        onClose={onCloseCanRequestCancelConfirmation} />
      <UserNotificationDialog show={pendingBulkRetry}
        title={translate(
          `${TRANSLATIONS}.canRequestsGrid.bulk.retryConfirmTitle`, {count: selectedCount}
        )}
        message={translate(
          `${TRANSLATIONS}.canRequestsGrid.bulk.retryConfirmMessage`, {count: selectedCount}
        )}
        actionLabel={translate('common:retry')}
        dismissLabel={translate('common:cancel')}
        onAction={onConfirmBulkRetry}
        onDismiss={onCloseBulkRetryConfirmation}
        onBackdropClick={onCloseBulkRetryConfirmation}
        loading={bulkRetrying}
        capitalize />
    </div>
  );
};

export default VehicleCanRequestsTab;
